<template>
  <div v-if="open" class="iv-drawer-scrim" @click="$emit('close')">
    <div class="iv-drawer" role="dialog" aria-modal="true" @click.stop>
      <!-- Header -->
      <div class="iv-drawer-head">
        <span class="iv-drawer-avatar">{{ initials }}</span>
        <div class="iv-drawer-headmeta">
          <div class="iv-drawer-titlerow">
            <h2 class="iv-drawer-name">{{ patientName }}</h2>
            <span
              class="iv-drawer-status"
              :style="{ background: statusChip.bg, color: statusChip.color }"
            >
              <i class="fa" :class="statusChip.icon" aria-hidden="true"></i>
              {{ statusChip.label }}
            </span>
          </div>
          <div class="iv-drawer-sub">
            <template v-if="dob">DOB {{ dob }} · </template>
            <span v-if="carrierName">{{ carrierName }}</span>
            <span v-else class="iv-muted">No carrier</span>
            <template v-if="memberId"> · Member <span class="iv-mono">{{ memberId }}</span></template>
            <template v-if="lastVerifiedRelative"> · Verified
              <span :title="lastVerifiedExact">{{ lastVerifiedRelative }}</span>
            </template>
          </div>
          <div v-if="lastCheckLine" class="iv-drawer-checknote">{{ lastCheckLine }}</div>
        </div>
        <div class="iv-drawer-actions">
          <!-- Disabled while the confirm panel is open: re-clicking it would re-arm a
               panel that is already showing, which is the same silent no-op the
               "Verify now" button above had. One live action at a time. -->
          <button
            type="button"
            class="iv-btn iv-btn--secondary"
            :disabled="verifyLoading || confirmOpen"
            @click="requestVerify"
          >
            <i v-if="verifyLoading" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <i v-else class="fa fa-refresh" aria-hidden="true"></i>
            {{ verifyLoading ? 'Checking…' : 'Re-check' }}
          </button>

          <!-- 6c: Print / Download PDF — shown ONLY for a verified row (Nilay's
               contract: non-verified rows must not offer a PDF). Both call
               get-pdf-url; the presigned handout opens in a new tab (a sync render
               can take ~2-3s, hence the spinner). -->
          <template v-if="showPdfButtons">
            <button
              type="button"
              class="iv-btn iv-btn--secondary"
              :disabled="!!pdfBusy"
              @click="openPdf('print')"
            >
              <i v-if="pdfBusy === 'print'" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
              <i v-else class="fa fa-print" aria-hidden="true"></i>
              {{ pdfBusy === 'print' ? 'Opening…' : 'Print for patient' }}
            </button>
            <button
              type="button"
              class="iv-btn iv-btn--primary"
              :disabled="!!pdfBusy"
              @click="openPdf('download')"
            >
              <i v-if="pdfBusy === 'download'" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
              <i v-else class="fa fa-download" aria-hidden="true"></i>
              {{ pdfBusy === 'download' ? 'Opening…' : 'Download PDF' }}
            </button>
          </template>

          <button type="button" class="iv-drawer-close" aria-label="Close" @click="$emit('close')">
            <i class="fa fa-times" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <!-- Verify confirmation — the "look before you spend" step.
           EVERY Stedi call bills, including one the payer rejects for bad data,
           so Verify Now no longer fires straight into a paid check: it first
           shows the Open-Dental-sourced values the 270 will carry. When the row
           carries a preflight `dataIssue` the backend will refuse this check for
           free, and we say so instead of quietly burning a call. -->
      <div v-if="confirmOpen" class="iv-confirm" :class="{ 'is-blocked': !!dataIssue }">
        <div class="iv-confirm-head">
          <i
            class="fa"
            :class="dataIssue ? 'fa-exclamation-triangle' : 'fa-paper-plane-o'"
            aria-hidden="true"
          ></i>
          <span class="iv-confirm-title">
            {{ confirmTitle }}
          </span>
        </div>

        <!--
          WHY THIS PANEL APPEARED. Since 2026-08-11 the table's Verify runs immediately
          for everyone EXCEPT an already-fresh patient, so when this panel opens from
          the table it is because the check looks redundant — and it has to say so, or
          it is just an unexplained speed bump on the page's primary action. `isFresh`
          means the nightly run would have skipped this patient as already covered.
        -->
        <div v-if="isRedundantRecheck" class="iv-confirm-redundant">
          Already verified <strong>{{ lastVerifiedRelative }}</strong
          ><template v-if="lastVerifiedExact"> ({{ lastVerifiedExact }})</template>, which is recent
          enough that the automatic nightly check skips this patient. Running it again bills a new check.
        </div>

        <!-- Blocked: the backend's own words, verbatim, plus what to do. -->
        <div v-if="dataIssue" class="iv-confirm-block">
          <div class="iv-confirm-block-detail">{{ dataIssue.detail }}</div>
          <div class="iv-confirm-block-fix">
            Fix this on the patient's insurance plan in Open Dental, then run the check again.
            Nothing is sent to the carrier and nothing is billed until the record is corrected.
          </div>
        </div>

        <div class="iv-confirm-sub">
          {{ dataIssue
            ? "Here's what Open Dental has on file for this patient today:"
            : 'This sends the following to the carrier. The check is billed whether or not the carrier finds the patient, so make sure it looks right:' }}
        </div>

        <div class="iv-confirm-facts">
          <div v-for="(f, i) in confirmFacts" :key="i" class="iv-confirm-fact">
            <div class="iv-confirm-fact-k">{{ f.k }}</div>
            <div class="iv-confirm-fact-v" :class="{ 'is-flagged': f.flag }">{{ f.v }}</div>
          </div>
        </div>

        <!-- Two plans = two billed checks. The office is about to spend money, so the
             count has to be on the button's own panel — not left to be discovered on
             the invoice. Only shown when this patient actually carries a second plan
             AND this particular click is unscoped (i.e. it will run both). A per-plan
             re-check costs one, and saying "billed twice" there would be a lie that
             trains people to ignore the warning. -->
        <div v-if="hasSecondaryPlan && !pendingOrdinal" class="iv-confirm-note iv-confirm-note--cost">
          <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
          This patient has a secondary plan ({{ secondaryCarrierName }}). Both plans are
          checked, so this runs <strong>2 separate checks</strong> and is billed twice.
        </div>

        <!-- Scoped re-check: name the plan, so nobody thinks they just refreshed both. -->
        <div v-if="pendingOrdinal" class="iv-confirm-note">
          <i class="fa fa-info-circle" aria-hidden="true"></i>
          This checks the <strong>{{ pendingOrdinal === 2 ? 'secondary' : 'primary' }}</strong>
          plan only ({{ pendingOrdinal === 2 ? secondaryCarrierName : (primaryCarrierLabel || 'primary plan') }})
          — <strong>1 check</strong>. The other plan keeps its current result.
        </div>

        <!-- The frontend does NOT know the resolved Stedi payer id for a
             never-verified patient (runEligibilityCheck resolves it server-side),
             so we say that rather than render a misleading blank. -->
        <div v-if="!payerId" class="iv-confirm-note">
          <i class="fa fa-info-circle" aria-hidden="true"></i>
          The payer ID and billing provider are resolved automatically when the check runs.
        </div>

        <div class="iv-confirm-actions">
          <!-- Blocked rows lead with "close and go fix it"; the spend button is
               demoted, but NOT disabled — see requestVerify(). -->
          <template v-if="dataIssue">
            <button type="button" class="iv-btn iv-btn--ghost" :disabled="verifyLoading" @click="confirmVerify">
              Run check anyway
            </button>
            <button type="button" class="iv-btn iv-btn--primary" @click="cancelVerify">
              <i class="fa fa-wrench" aria-hidden="true"></i>
              Close &amp; fix in Open Dental
            </button>
          </template>
          <template v-else>
            <button type="button" class="iv-btn iv-btn--secondary" @click="cancelVerify">Cancel</button>
            <button type="button" class="iv-btn iv-btn--primary" :disabled="verifyLoading" @click="confirmVerify">
              <i v-if="verifyLoading" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
              <i v-else class="fa fa-bolt" aria-hidden="true"></i>
              {{ verifyLoading ? 'Checking…' : 'Run check' }}
            </button>
          </template>
        </div>
      </div>

      <!-- Verify-flow message (inline, non-scary) -->
      <div v-if="verifyMessage" class="iv-drawer-verifymsg" :class="`is-${verifyType}`">
        <i class="fa" :class="verifyMsgIcon" aria-hidden="true"></i>
        <span>{{ verifyMessage }}</span>
      </div>

      <!-- Plan tabs — ONLY for a patient who actually carries a second plan, so a
           single-plan drawer looks exactly as it always has. Coordination of
           benefits is decided by the SECONDARY plan's coverage, so "the second plan
           was verified" (which the row already said) is not enough — the front desk
           needs to see what it actually covers in order to quote the patient. -->
      <div v-if="hasSecondaryPlan" class="iv-plan-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          class="iv-plan-tab"
          :class="{ 'is-active': activeOrdinal === 1 }"
          :aria-selected="activeOrdinal === 1 ? 'true' : 'false'"
          @click="selectPlan(1)"
        >
          Primary
          <span v-if="primaryCarrierLabel" class="iv-plan-tab-sub">{{ primaryCarrierLabel }}</span>
        </button>
        <button
          type="button"
          role="tab"
          class="iv-plan-tab"
          :class="{ 'is-active': activeOrdinal === 2 }"
          :aria-selected="activeOrdinal === 2 ? 'true' : 'false'"
          @click="selectPlan(2)"
        >
          Secondary
          <span class="iv-plan-tab-sub">{{ secondaryCarrierName }}</span>
        </button>

        <!-- Refresh ONE plan. The header's "Re-check" deliberately still runs both
             (that is what the table's Verify does too, and narrowing it would mean
             the secondary silently stopped being refreshed) — this is the cheaper,
             explicit alternative when only one plan is stale. -->
        <button
          type="button"
          class="iv-plan-tab-action"
          :disabled="verifyLoading"
          @click="requestVerify(activeOrdinal)"
        >
          <i class="fa fa-refresh" aria-hidden="true"></i>
          Re-check this plan only
        </button>
      </div>

      <!-- Body -->
      <div class="iv-drawer-body">
        <!-- Loading -->
        <div v-if="activeDetail.loading" class="iv-drawer-state">
          <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
          Loading benefits…
        </div>

        <!-- Error -->
        <div v-else-if="activeDetail.error" class="iv-drawer-state iv-drawer-state--error">
          <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
          <div class="iv-drawer-state-title">Couldn't load these benefits</div>
          <div class="iv-drawer-state-sub">{{ activeDetail.error }}</div>
          <button type="button" class="iv-btn iv-btn--secondary" @click="$emit('retry')">
            <i class="fa fa-refresh" aria-hidden="true"></i> Retry
          </button>
        </div>

        <!--
          Not verified yet (404) — but "not verified" has TWO meanings and they need
          opposite calls to action.

          A refused check persists no row, so `activeDetail.notFound` stays true after a
          terminal refusal too. Until 2026-08-11 this block then rendered "This patient
          hasn't been checked yet. Run a verification…" with a green Verify now button
          DIRECTLY BELOW the amber banner saying the payer does not support electronic
          verification and to phone them. Two panels contradicting each other, and the
          louder one invited a click that can only ever refuse again. Caught on the Mint
          prod pilot with a Guardian INDIVIDUAL PLAN (payer GI813).
        -->
        <div v-else-if="activeDetail.notFound" class="iv-drawer-state">
          <div class="iv-drawer-state-icon">
            <i class="fa" :class="terminalRefusal ? 'fa-phone' : 'fa-shield'" aria-hidden="true"></i>
          </div>

          <template v-if="terminalRefusal">
            <div class="iv-drawer-state-title">Can't be verified electronically</div>
            <!-- The banner above already carries the payer's name and the "no charge"
                 wording; repeating it here would just be louder, not clearer. -->
            <div class="iv-drawer-state-sub">
              This plan has to be verified by phone. Checking again won't help — the payer
              doesn't offer electronic eligibility at all.
            </div>
          </template>

          <template v-else>
            <div class="iv-drawer-state-title">Not verified yet</div>
            <div class="iv-drawer-state-sub">
              This patient hasn't been checked yet. Run a verification to pull live benefits.
            </div>

            <!--
              Shown only when a check is actually possible AND nothing else already owns
              the action. Hidden while the confirm panel is open because both used to
              render at once and this button was a literal no-op then — requestVerify()
              only sets confirmOpen, which was already true.
            -->
            <button
              v-if="hasInsurance && !confirmOpen"
              type="button"
              class="iv-btn iv-btn--primary"
              :disabled="verifyLoading"
              @click="requestVerify"
            >
              <i v-if="verifyLoading" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
              <i v-else class="fa fa-bolt" aria-hidden="true"></i>
              {{ verifyLoading ? 'Checking…' : 'Verify now' }}
            </button>
            <!-- Keyed on hasInsurance ALONE. It used to be the `v-else` of the button's
                 whole condition, so once that gained `&& !confirmOpen` an insured patient
                 with the confirm panel open was told "No insurance on file to verify." -->
            <div v-else-if="!hasInsurance" class="iv-drawer-state-sub iv-muted">
              No insurance on file to verify.
            </div>
          </template>
        </div>

        <!-- Loaded -->
        <div v-else-if="verification" class="iv-drawer-loaded">
          <!-- Jump to -->
          <nav class="iv-jump-nav">
            <div class="iv-jump-title">Jump to</div>
            <button
              v-for="sec in navSections"
              :key="sec.id"
              type="button"
              class="iv-jump-link"
              :class="{ 'is-active': activeSection === sec.id }"
              @click="scrollTo(sec.id)"
            >
              {{ sec.label }}
            </button>
          </nav>

          <!-- Scrollable content -->
          <div ref="scrollBody" class="iv-drawer-body-scroll" :class="{ 'has-netbar': networkSplit }">
            <!-- Network view (2026-09-29). Sticky so the reader always knows which network the
                 figures below belong to. Hidden when this check has no per-network split. -->
            <div v-if="networkSplit" class="iv-netbar" data-test="iv-network-switch">
              <span class="iv-netbar-label">Show benefits</span>
              <div class="iv-seg" role="radiogroup" aria-label="Show benefits for">
                <button
                  v-for="opt in networkOptions"
                  :key="opt.value"
                  type="button"
                  role="radio"
                  class="iv-seg-btn"
                  :class="{ 'is-active': shownNetwork === opt.value }"
                  :aria-checked="shownNetwork === opt.value ? 'true' : 'false'"
                  :data-test="'iv-network-' + opt.value"
                  @click="pickNetworkView(opt.value)"
                >{{ opt.label }}</button>
              </div>
              <span class="iv-netbar-note">Print and PDF always show both networks.</span>
            </div>
            <div v-else-if="preSplitNote" class="iv-netbar-presplit" data-test="iv-network-presplit">
              <i class="fa fa-info-circle" aria-hidden="true"></i>
              {{ preSplitNote }}
            </div>

            <!-- Plan summary -->
            <section id="iv-sec-summary" class="iv-sec">
              <div class="iv-sec-title">
                Plan summary
                <span v-if="singleNetwork" class="iv-net-pill">{{ shownNetworkLabel }}</span>
              </div>
              <div class="iv-rings-grid">
                <BenefitRing v-for="r in rings" :key="r.key" :view="r" />
              </div>
              <div class="iv-facts-grid">
                <div v-for="(f, i) in planFacts" :key="i" class="iv-fact">
                  <div class="iv-fact-k">{{ f.k }}</div>
                  <div class="iv-fact-v">{{ f.v }}</div>
                </div>
              </div>
            </section>

            <!-- Coverage alerts -->
            <section v-if="warnings.length" id="iv-sec-warnings" class="iv-sec">
              <div class="iv-sec-title">Coverage alerts</div>
              <div class="iv-warn-list">
                <div
                  v-for="(w, i) in warnings"
                  :key="i"
                  class="iv-warn-row"
                  :style="warnStyle(w)"
                >
                  <i class="fa" :class="warnIcon(w)" :style="{ color: warnColor(w) }" aria-hidden="true"></i>
                  <div>
                    <div class="iv-warn-title">{{ w.title || w.code }}</div>
                    <div v-if="w.detail" class="iv-warn-detail">{{ w.detail }}</div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Maximums & deductibles -->
            <section v-if="maximums.length" id="iv-sec-maximums" class="iv-sec">
              <div class="iv-sec-title">
                Maximums &amp; deductibles
                <span v-if="singleNetwork" class="iv-net-pill">{{ shownNetworkLabel }}</span>
              </div>
              <div class="iv-mtable">
                <div class="iv-mtable-head">
                  <span>Benefit</span>
                  <span class="iv-right">Plan</span>
                  <span class="iv-right">Used</span>
                  <span class="iv-right">Remaining</span>
                </div>
                <!--
                  `unlimited` = the payer reported no maximum (a 99999999.99-style
                  sentinel, normalized away by presenter-271.js). PLAN says so in words;
                  REMAINING stays an em-dash because "remaining" against no maximum is
                  meaningless. USED is untouched — it is real spend and the only figure
                  on the row worth reading. Before this, the row printed the sentinel and
                  a derived remaining of $99,999,087.59.
                -->
                <div v-for="(m, i) in maximums" :key="i" class="iv-mtable-row">
                  <span class="iv-mlabel">{{ m.label }}</span>
                  <!-- Single-network view, and the payer sent nothing for that network: say so.
                       Never the other network's figure standing in (decision 2026-09-29). -->
                  <span v-if="m.notReported" class="iv-right iv-nomax" :title="notReportedTitle">Not reported</span>
                  <span v-else-if="m.unlimited" class="iv-right iv-nomax">No maximum</span>
                  <span v-else class="iv-right iv-mono">{{ money(m.planAmount) }}</span>
                  <span class="iv-right iv-mono iv-muted">{{ m.usedAmount !== null && m.usedAmount !== undefined ? money(m.usedAmount) : '—' }}</span>
                  <span class="iv-right iv-mono iv-mremaining" :style="{ color: remainingColor(m) }">
                    {{ m.unlimited ? '—' : (m.hasRemaining ? money(m.remainingAmount) : '—') }}
                  </span>
                </div>
              </div>
            </section>

            <!-- Benefits by category -->
            <section v-if="categories.length" id="iv-sec-benefits" class="iv-sec">
              <div class="iv-sec-title">
                Benefits by category
                <span v-if="singleNetwork" class="iv-net-pill">{{ shownNetworkLabel }}</span>
              </div>
              <div class="iv-ctable">
                <div class="iv-ctable-head">
                  <span>Category</span>
                  <span class="iv-right">Covered</span>
                  <span class="iv-right">Network</span>
                </div>
                <div v-for="(c, i) in categoryRows" :key="i" class="iv-ctable-row">
                  <span class="iv-cname">{{ c.name }}</span>
                  <span class="iv-right iv-ccov">{{ pct(c.percent) }}</span>
                  <span
                    class="iv-right iv-cnet"
                    :class="{ 'iv-muted': c.notReported }"
                    :title="c.notReported ? notReportedTitle : null"
                  >{{ c.networkText }}</span>
                </div>
              </div>
            </section>

            <!-- Procedure coverage — per-code detail from the 271 presenter
                 Summary. Grouped by category; the whole section (and its jump
                 link) is omitted when the payer returned no per-code data
                 (procedures === []), mirroring the other optional arrays. -->
            <section v-if="hasProcedureContent" id="iv-sec-procedures" class="iv-sec">
              <div class="iv-sec-title">
                Procedure coverage
                <span v-if="singleNetwork" class="iv-net-pill">{{ shownNetworkLabel }}</span>
              </div>
              <!-- Both view: most plans pay the same in and out of network, so two identical
                   columns hide the few rows that differ. Equal figures merge into one; the
                   rows that differ are highlighted. -->
              <p v-if="!singleNetwork" class="iv-netlegend">
                One figure across both columns = the same in and out of network.
                <span class="iv-netlegend-diff">Highlighted</span> rows differ.
              </p>
              <div class="iv-ptable" :class="{ 'is-single': singleNetwork }">
                <div class="iv-ptable-head">
                  <span>Code</span>
                  <span>Procedure</span>
                  <template v-if="singleNetwork">
                    <span class="iv-right">{{ shownNetwork === 'in' ? 'In %' : 'Out %' }}</span>
                  </template>
                  <template v-else>
                    <span class="iv-right">In %</span>
                    <span class="iv-right">Out %</span>
                  </template>
                  <span>Allowance</span>
                  <span class="iv-right">Last service</span>
                </div>
                <template v-for="(g, gi) in procedureGroups">
                  <div :key="'pg-' + gi" class="iv-ptable-group">{{ g.category }}</div>
                  <!-- Two-line row: the aligned facts, then (only when the payer sent
                       limitations for THIS code) a chip line spanning the full width.
                       Deliberately not an accordion — a front desk with a patient waiting
                       will not click 42 rows, and the hidden facts (deductible waived,
                       shared counters) are the ones that cause denials. Zero added height
                       when a code has no notes. -->
                  <div
                    v-for="(p, pi) in g.rows"
                    :key="'pg-' + gi + '-r-' + pi"
                    class="iv-prow"
                  >
                    <div
                      class="iv-ptable-row"
                      :class="{ 'is-netdiff': !singleNetwork && !samePct(p.inPercent, p.outPercent) }"
                    >
                      <span class="iv-mono iv-hcode">{{ p.code || '—' }}</span>
                      <span>{{ p.description || p.code || '—' }}</span>
                      <span
                        v-if="singleNetwork"
                        class="iv-right iv-ccov"
                        :class="{ 'is-estimate': p.isEstimate }"
                        :title="sideTitle(p)"
                      >{{ pctCell(sidePct(p), p.isEstimate) }}</span>
                      <span
                        v-else-if="samePct(p.inPercent, p.outPercent)"
                        class="iv-ccov iv-pct-merged"
                        :class="{ 'is-estimate': p.isEstimate }"
                        :title="p.isEstimate ? ESTIMATE_TITLE : null"
                      >{{ pctCell(p.inPercent, p.isEstimate) }}</span>
                      <template v-else>
                        <span
                          class="iv-right iv-ccov"
                          :class="{ 'is-estimate': p.isEstimate }"
                          :title="p.isEstimate ? ESTIMATE_TITLE : null"
                        >{{ pctCell(p.inPercent, p.isEstimate) }}</span>
                        <span
                          class="iv-right iv-ccov"
                          :class="{ 'is-estimate': p.isEstimate }"
                          :title="p.isEstimate ? ESTIMATE_TITLE : null"
                        >{{ pctCell(p.outPercent, p.isEstimate) }}</span>
                      </template>
                      <span class="iv-pfreq">
                        <span v-if="freq(p.frequency)">{{ freq(p.frequency) }}</span>
                        <!--
                          The payer returned no frequency for a code this office SPECIFICALLY
                          asked about. Silence on a requested code is itself the answer — it
                          means phone the payer — whereas silence on the other ~30 codes is
                          just noise, so those keep the quiet em-dash below.
                        -->
                        <span
                          v-else-if="!left(p) && wasRequested(p.code)"
                          class="iv-freq-unreported"
                          title="This payer did not return a frequency limit for this code. Call the payer to confirm."
                        >Not reported</span>
                        <span v-else-if="!left(p)" >—</span>
                        <span
                          v-if="left(p)"
                          class="iv-left-pill"
                          :class="'is-' + leftTone(p)"
                        >{{ left(p) }}</span>
                      </span>
                      <span class="iv-right iv-muted">{{ procDate(p.lastServiceDate) || '—' }}</span>
                    </div>
                    <div v-if="noteChips(p).length" class="iv-pnotes">
                      <span
                        v-for="(c, ci) in noteChips(p)"
                        :key="'nc-' + ci"
                        class="iv-nchip"
                        :class="'is-' + c.tone"
                        :title="c.title"
                      >{{ c.label }}</span>
                    </div>
                  </div>
                </template>
              </div>

              <!-- Same footnote the handout prints, in the same words. The asterisk has to
                   mean the same thing on both surfaces or it means nothing. -->
              <p v-if="hasEstimate" class="iv-pfootnote">
                * Estimated from category coverage; your plan did not return a benefit specific to this code.
              </p>

              <!-- Codes the PAYER reported that this office does not track. COLLAPSED: one prod
                   AARP patient carried 392 of these against the office's 23 configured codes, and
                   rendering them inline is what made this table unreadable. Never hidden outright
                   though — silently dropping them is the other half of the bug this fixes. -->
              <div v-if="payerExtras.length" class="iv-pextras">
                <button type="button" class="iv-pextras-toggle" @click="showPayerExtras = !showPayerExtras">
                  <i class="fa" :class="showPayerExtras ? 'fa-caret-down' : 'fa-caret-right'" aria-hidden="true"></i>
                  Also reported by this payer ({{ payerExtras.length }})
                </button>
                <p class="iv-pextras-hint">
                  Codes your plan returned that aren't on your Precheck settings list.
                </p>
                <div v-if="showPayerExtras" class="iv-ptable" :class="{ 'is-single': singleNetwork }">
                  <div class="iv-ptable-head">
                    <span>Code</span>
                    <span>Procedure</span>
                    <template v-if="singleNetwork">
                      <span class="iv-right">{{ shownNetwork === 'in' ? 'In %' : 'Out %' }}</span>
                    </template>
                    <template v-else>
                      <span class="iv-right">In %</span>
                      <span class="iv-right">Out %</span>
                    </template>
                    <span>Allowance</span>
                    <span class="iv-right">Last service</span>
                  </div>
                  <template v-for="(g, gi) in payerExtraGroups">
                    <div :key="'xg-' + gi" class="iv-ptable-group">{{ g.category }}</div>
                    <div v-for="(p, pi) in g.rows" :key="'xg-' + gi + '-r-' + pi" class="iv-prow">
                      <div
                        class="iv-ptable-row"
                        :class="{ 'is-netdiff': !singleNetwork && !samePct(p.inPercent, p.outPercent) }"
                      >
                        <span class="iv-mono iv-hcode">{{ p.code || '—' }}</span>
                        <span>{{ p.description || p.code || '—' }}</span>
                        <span v-if="singleNetwork" class="iv-right iv-ccov" :title="sideTitle(p)">{{ pctCell(sidePct(p), p.isEstimate) }}</span>
                        <span v-else-if="samePct(p.inPercent, p.outPercent)" class="iv-ccov iv-pct-merged">{{ pctCell(p.inPercent, p.isEstimate) }}</span>
                        <template v-else>
                          <span class="iv-right iv-ccov">{{ pctCell(p.inPercent, p.isEstimate) }}</span>
                          <span class="iv-right iv-ccov">{{ pctCell(p.outPercent, p.isEstimate) }}</span>
                        </template>
                        <span class="iv-pfreq">
                          <span v-if="freq(p.frequency)">{{ freq(p.frequency) }}</span>
                          <span v-else-if="!left(p)">—</span>
                          <span v-if="left(p)" class="iv-left-pill" :class="'is-' + leftTone(p)">{{ left(p) }}</span>
                        </span>
                        <span class="iv-right iv-muted">{{ procDate(p.lastServiceDate) || '—' }}</span>
                      </div>
                      <div v-if="noteChips(p).length" class="iv-pnotes">
                        <span
                          v-for="(c, ci) in noteChips(p)"
                          :key="'xnc-' + ci"
                          class="iv-nchip"
                          :class="'is-' + c.tone"
                          :title="c.title"
                        >{{ c.label }}</span>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </section>

            <!-- Waiting periods -->
            <section v-if="waitingPeriods.length" id="iv-sec-waiting" class="iv-sec">
              <div class="iv-sec-title">Waiting periods</div>
              <div class="iv-waiting-grid">
                <div v-for="(wp, i) in waitingPeriods" :key="i" class="iv-waiting-row">
                  <i
                    class="fa"
                    :class="wp.applies ? 'fa-clock-o' : 'fa-check-circle'"
                    :style="{ color: wp.applies ? '#b45309' : '#0f7259' }"
                    aria-hidden="true"
                  ></i>
                  <div class="iv-waiting-name">{{ wp.name }}</div>
                  <span
                    class="iv-waiting-text"
                    :style="{ color: wp.applies ? '#b45309' : '#717977' }"
                    :title="wp.applies ? wp.text : ''"
                  >{{ wp.applies ? 'Applies' : 'None' }}</span>
                </div>
              </div>
            </section>

            <!-- Plan notes — the messages that belong to the PLAN, not to a procedure.
                 Per-code limitations now live on their own row above; what remains here is
                 the handful of genuinely plan-level facts (missing-tooth clause, dependent
                 age, network names). Falls back to the old flat list for verifications
                 stored before the presenter started emitting planNotes. -->
            <section v-if="planNotes.length" id="iv-sec-limitations" class="iv-sec">
              <div class="iv-sec-title">Plan notes</div>
              <ul class="iv-limit-list">
                <li v-for="(l, i) in planNotes" :key="i">{{ l }}</li>
              </ul>
            </section>

            <!--
              Payer-reported last service dates from the 271 Summary (deduped +
              most-recent-first by the presenter).

              There used to be a SECOND "Service history" section directly below this one,
              rendering this patient's Open Dental procedurelog verbatim. It was removed
              2026-08-12: the front desk already has that chart open in Open Dental, so it
              added nothing — and sitting adjacent to this payer-sourced panel, the two got
              read as one contradictory list (the confusion that surfaced on Saba Khan's
              drawer during the Mint pilot).

              The office chart is still queried, just not re-displayed. `/detail` diffs it
              against these rows and returns `inOfficeChart` per row, which surfaces the one
              thing Open Dental genuinely cannot: a service the PAYER has on record that this
              office never performed happened somewhere else — and it still counts against
              the patient's frequency allowance.
            -->
            <section v-if="summaryServiceHistory.length" id="iv-sec-summary-history" class="iv-sec">
              <!--
                WHOSE record this is, in the TITLE itself. The benefits belong to the POLICY
                HOLDER's plan, so on a dependent's drawer these dates are only the patient's
                own when the payer actually answered about them. The attribution used to live
                only in the note below and read as a footnote — a spouse's cleaning dates
                still scanned as the patient's, which is what the pilot tripped over.

                The name renders in its natural case against the all-caps heading, and in RED
                only when the holder is somebody OTHER than this patient. A self-subscriber's
                own name in red would teach the front desk that red means nothing.
              -->
              <div class="iv-sec-title">
                <template v-if="policyHolderName">
                  Policy holder:
                  <span class="iv-holder" :class="{ 'iv-holder--other': subjectScope === 'subscriber' }">{{ policyHolderName }}</span>
                  <span class="iv-title-sep">—</span>
                </template>
                Recent services (per insurance){{ policyHolderName ? ':' : '' }}
              </div>
              <div v-if="historyAttribution" class="iv-sec-sub" :class="{ 'iv-sec-sub--warn': subjectScope === 'subscriber' }">
                {{ historyAttribution }}
              </div>
              <div class="iv-shtable">
                <div class="iv-shtable-head">
                  <span>Service</span>
                  <span class="iv-right">Last service</span>
                </div>
                <div v-for="(h, i) in summaryServiceHistory" :key="i" class="iv-shtable-row">
                  <span>
                    {{ h.label || h.code || h.category || '—' }}
                    <!-- Only an explicit `false` renders. `null` means we could not tell
                         (no CDT code, or the chart lookup failed) and must stay silent
                         rather than imply the office missed something.

                         `showsChartDiff` additionally suppresses the badge on subscriber
                         scope, where the rows describe a different person entirely and
                         "we didn't do this" is trivially true of all of them. The service
                         already withholds the flag there; this is the second lock, because
                         the noise lands on dependents — the patients the panel matters
                         most for. -->
                    <span
                      v-if="showsChartDiff && h.inOfficeChart === false"
                      class="iv-elsewhere"
                      :title="elsewhereTitle"
                    >
                      not in this office's chart
                    </span>
                  </span>
                  <span class="iv-right iv-muted">{{ procDate(h.lastServiceDate) || '—' }}</span>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions } from 'vuex';
import BenefitRing from './BenefitRing.vue';
import {
  RING_DEFS,
  ringView,
  normalizeMaximumRow,
  isTerminalVerifyReason,
  buildPlanFacts,
  statusPresentation,
  networkLabel,
  formatMoney,
  formatProcDate,
  formatFrequency,
  formatRemaining,
  remainingSeverity,
  noteChipLabel,
  noteChipTone,
  formatAgeLimit,
  formatCheckedAt,
  relativeFromNow,
  lastCheckNote,
  chipForCode,
  chipColors,
  rowDataIssue,
  resolveNetworkView,
  networkViewLabel,
  hasNetworkSplit,
  maximumsForView,
  ringSourcesForView,
  categoryPercentForView,
  samePercent,
} from './constants.js';

export default {
  name: 'VerificationDrawer',
  components: { BenefitRing },
  props: {
    open: { type: Boolean, default: false },
    // The dashboard row that opened the drawer — the source of the patient's
    // identity (name/DOB) which the verification row itself doesn't carry.
    row: { type: Object, default: null },
    // Used by the live PDF buttons (get-pdf-url) — the drawer owns that fetch.
    siteId: { type: String, default: '' },
    timezone: { type: String, default: '' },
    // { loading, message, type } for the current patient's verify flow (from the page).
    verifyState: { type: Object, default: null },
    // A monotonically increasing counter the page bumps when the TABLE's inline
    // Verify button is clicked. Any change arms this drawer's confirmation panel —
    // see the watcher for why it is a counter and not a boolean.
    confirmRequest: { type: Number, default: 0 },
    /*
     * The CDT codes this office explicitly asked to verify (Precheck settings → Procedure
     * codes). Used only to decide where a MISSING frequency is worth saying out loud.
     *
     * ⛔ An explicit list ONLY — null/absent means the office never customised it and is
     * running Teamio's default set, which is OUR choice, not a request they made. Treating
     * that as "they asked" would print "not reported" on ~50 codes nobody enquired about,
     * which is the noise this scoping exists to avoid.
     */
    requestedCodes: { type: Array, default: null },
    /*
     * The office's default benefits view — settings blob `benefitsNetworkView`
     * ('both' | 'in' | 'out'). Only where the drawer STARTS: staff can flip it inside the
     * drawer, and that choice holds while this page stays open (the component outlives
     * open/close), resetting to this default on reload.
     */
    defaultNetworkView: { type: String, default: 'both' },
  },

  data() {
    return {
      activeSection: 'iv-sec-summary',
      // Payer-reported codes the office does not track. Collapsed by default — see the
      // template: one prod patient's payer sent 392 of them.
      showPayerExtras: false,
      // Which PDF button is fetching ('print' | 'download' | null) — drives the
      // per-button spinner and disables both while a presign is in flight.
      pdfBusy: null,
      // Verify Now is a two-step action: the first click opens this confirmation
      // panel (what will be sent + any preflight data issue), and only the
      // Confirm inside it emits 'verify' and spends money.
      confirmOpen: false,
      // Which plan slot the body is showing: 1 = primary, 2 = secondary. Only
      // reachable when the patient carries a second plan; always reset to 1 on a
      // patient change so a drawer never opens on someone else's chosen tab.
      activeOrdinal: 1,
      // SCOPE of the pending check, captured when the confirm panel is armed:
      // null = every plan the patient carries (the header button and the table
      // button — historical behaviour), 1 or 2 = that plan only.
      // Captured at ARM time, not read at confirm time, so switching tabs while the
      // panel is open cannot silently re-aim what the Confirm button pays for.
      pendingOrdinal: null,
      // Which network the benefit sections show — see the defaultNetworkView prop.
      networkView: resolveNetworkView(this.defaultNetworkView),
      // Set once staff pick a view by hand, so settings that finish loading AFTER the drawer
      // mounted can still apply the office default without overriding a choice just made.
      networkViewPicked: false,
    };
  },

  computed: {
    ...mapState('insuranceVerification', {
      detail: (state) => state.detail,
      secondaryDetail: (state) => state.secondaryDetail,
    }),

    /**
     * The detail slice the body is currently rendering. EVERY downstream computed
     * reads through `verification`/`summary`, which read through this — so the one
     * binding here is what makes the whole ~700 lines of benefit rendering work for
     * either plan without duplicating any of it.
     */
    activeDetail() {
      return this.activeOrdinal === 2 ? this.secondaryDetail : this.detail;
    },

    verification() {
      return this.activeDetail.verification || null;
    },
    summary() {
      return (this.verification && this.verification.Summary) || null;
    },

    patient() {
      return (this.row && this.row.patient) || {};
    },
    patientName() {
      const f = this.patient.fName || '';
      const l = this.patient.lName || '';
      return `${f} ${l}`.trim() || 'Patient';
    },
    initials() {
      const a = (this.patient.fName && String(this.patient.fName)[0]) || '';
      const b = (this.patient.lName && String(this.patient.lName)[0]) || '';
      return ((a + b).toUpperCase()) || '?';
    },
    dob() {
      return this.odDob(this.patient.dob);
    },

    /**
     * The row-shaped object for the ACTIVE plan. `row.secondary` carries the same
     * `{ insurance, verification }` shape as the row itself (see the backend's
     * dashboard-assemble), which is what lets every header/fallback read below stay
     * one expression instead of branching per plan.
     *
     * Falls back to the primary row when there is no secondary — so on a
     * single-plan patient this is just `row`, exactly as before.
     */
    activePlanRow() {
      if (this.activeOrdinal === 2 && this.row && this.row.secondary) return this.row.secondary;
      return this.row;
    },

    hasInsurance() {
      const ins = (this.activePlanRow && this.activePlanRow.insurance) || {};
      return ins.hasInsurance !== false;
    },
    carrierName() {
      const plan = (this.summary && this.summary.plan) || {};
      return plan.carrierName
        || (this.verification && this.verification.CarrierName)
        || ((this.activePlanRow && this.activePlanRow.insurance && this.activePlanRow.insurance.carrierName) || '');
    },
    memberId() {
      const plan = (this.summary && this.summary.plan) || {};
      return plan.memberId
        || (this.verification && this.verification.MemberID)
        || ((this.activePlanRow && this.activePlanRow.insurance && this.activePlanRow.insurance.memberId) || '');
    },

    // The primary tab's sub-label. Reads the row directly (not `carrierName`,
    // which follows the ACTIVE plan) so the tab keeps naming the primary's carrier
    // while you're looking at the secondary.
    primaryCarrierLabel() {
      return (this.row && this.row.insurance && this.row.insurance.carrierName) || '';
    },

    // ── Verify confirmation ────────────────────────────────────────────────
    /**
     * The row's OD preflight block ({ code, detail }) or null. Sourced from the
     * DASHBOARD row, which screens the member ID only (`screenMemberId`) — the
     * orchestrator additionally screens subscriber name/DOB at check time, so a
     * clean row here is not a promise the check will run.
     */
    dataIssue() {
      return rowDataIssue(this.activePlanRow);
    },

    /**
     * Stedi payer id, ONLY when we genuinely have one from a prior verification.
     * For a never-verified patient this is resolved backend-side (carrier →
     * ElectID / site override / global map) and the frontend cannot know it —
     * so this stays empty and the panel says so rather than guessing.
     */
    payerId() {
      const plan = (this.summary && this.summary.plan) || {};
      return plan.payerId || (this.verification && this.verification.PayerID) || '';
    },

    /**
     * Subscriber DOB, if a prior verification carried one. NOTE: today's
     * presenter Summary does not expose it (the 270's subscriber DOB comes off
     * the OD chain at check time), so this is normally empty and the row is
     * simply omitted. Kept guarded so it lights up for free if the backend ever
     * surfaces it — never fabricated from the patient's own DOB, which is a
     * DIFFERENT person whenever the patient isn't the subscriber.
     */
    subscriberDob() {
      const plan = (this.summary && this.summary.plan) || {};
      return this.odDob(plan.subscriberDob);
    },

    /**
     * Does this patient carry a SECONDARY plan (patplan.Ordinal = 2)? Drives the
     * two-checks cost warning on the confirm panel. Reads from the dashboard row, which
     * is the only place the frontend learns about the second plan before a check runs.
     * Absent on an older backend → false → the warning simply doesn't render.
     */
    hasSecondaryPlan() {
      const sec = this.row && this.row.secondary;
      return !!(sec && sec.insurance && sec.insurance.hasInsurance);
    },

    secondaryCarrierName() {
      const sec = this.row && this.row.secondary;
      return (sec && sec.insurance && sec.insurance.carrierName) || 'second plan';
    },

    /**
     * The compact "what will be sent" grid. Uses the same `—`-for-unavailable
     * idiom as buildPlanFacts' val(), and OMITS payer id / subscriber DOB
     * entirely when unknown rather than rendering a blank that reads as "we will
     * send nothing here".
     */
    confirmFacts() {
      const val = (x) => (x === null || x === undefined || x === '' ? '—' : String(x));
      const issue = this.dataIssue;
      const memberIdFlagged = !!(issue
        && (issue.code === 'MISSING_MEMBER_ID' || issue.code === 'PLACEHOLDER_MEMBER_ID'));

      // The carrier's own Electronic ID is the broken field — flag the Payer ID row
      // instead of the Member ID one, so the confirm grid points at what to fix.
      const payerIdFlagged = !!(issue && issue.code === 'UNRECOGNIZED_PAYER_ID');

      const facts = [
        { k: 'Patient', v: val(this.patientName) },
        { k: 'Carrier', v: val(this.carrierName), flag: payerIdFlagged },
        { k: 'Member ID', v: val(this.memberId), flag: memberIdFlagged },
      ];
      if (this.payerId) facts.push({ k: 'Payer ID', v: String(this.payerId), flag: payerIdFlagged });
      if (this.subscriberDob) facts.push({ k: 'Subscriber DOB', v: String(this.subscriberDob) });
      return facts;
    },

    resolvedStatus() {
      if (this.verification && this.verification.Status) return this.verification.Status;
      return (this.activePlanRow && this.activePlanRow.verification && this.activePlanRow.verification.status) || null;
    },
    statusChip() {
      return statusPresentation(this.resolvedStatus, { hasInsurance: this.hasInsurance });
    },
    showPdfButtons() {
      return this.resolvedStatus === 'verified';
    },
    // Follows the ACTIVE plan, which is also what makes Print / Download PDF
    // per-plan for free: the handout endpoint is keyed by verification id, so the
    // Secondary tab's buttons presign the secondary plan's own document.
    verificationID() {
      return (this.verification && this.verification.VerificationID) || this.activeDetail.verificationId || null;
    },

    /**
     * The active plan's last SUCCESSFUL verification. ⛔ Not the /detail row's CheckedAt on its
     * own: that is the latest check of ANY status, so a transport error read "verified a few
     * seconds ago" (2026-09-23). Each plan reads its own row's value.
     */
    lastVerifiedRaw() {
      // Never the primary's value on the Secondary tab (activePlanRow falls back to the row).
      const planRow = this.activeOrdinal === 2 ? ((this.row && this.row.secondary) || null) : this.row;
      // A verified /detail row is our newest check — newer than a row that predates a re-check.
      if (this.verification && this.verification.Status === 'verified' && this.verification.CheckedAt) {
        return this.verification.CheckedAt;
      }
      if (planRow && planRow.lastVerified) return planRow.lastVerified;
      if (this.activeDetail.odLastVerified) return this.activeDetail.odLastVerified;
      return (planRow && planRow.odLastVerified) || null;
    },
    // The latest check, as dashboard-shaped fields: the fetched /detail row when there is one
    // (it can be newer than the row after a re-check), else the plan row's own.
    latestCheck() {
      const d = this.verification;
      if (d && d.Status) return { status: d.Status, errorType: d.ErrorType || null, checkedAt: d.CheckedAt || null };
      return (this.activePlanRow && this.activePlanRow.verification) || null;
    },
    lastCheckLine() {
      const d = this.verification;
      return lastCheckNote({
        verification: this.latestCheck,
        lastVerified: this.lastVerifiedRaw,
        lastChecked: (d && d.CheckedAt) || (this.activePlanRow && this.activePlanRow.lastChecked) || null,
        carrierName: this.carrierName,
        tz: this.timezone,
      });
    },
    /**
     * Is this confirm being shown because the check looks REDUNDANT (rather than
     * because the row has a data issue)? `row.isFresh` is the backend's own
     * "already verified recently" verdict — computeRowFreshness() in
     * dashboard-assemble.js, resolved against the site's settings.frequencyMonths,
     * and the same signal the nightly runner uses to skip a patient.
     *
     * Guarded on lastVerifiedRelative too: a "verified <blank> ago" line is worse
     * than no line, so if we cannot name WHEN, fall back to the generic prompt.
     */
    isRedundantRecheck() {
      return !!(this.row && this.row.isFresh && !this.dataIssue && this.lastVerifiedRelative);
    },

    confirmTitle() {
      if (this.dataIssue) return 'This check would be refused — no charge';
      if (this.isRedundantRecheck) return 'This patient was checked recently — check again?';
      return 'Run an eligibility check?';
    },

    lastVerifiedRelative() {
      return this.lastVerifiedRaw ? relativeFromNow(this.lastVerifiedRaw, this.timezone) : '';
    },
    lastVerifiedExact() {
      return this.lastVerifiedRaw ? formatCheckedAt(this.lastVerifiedRaw, this.timezone) : '';
    },

    /**
     * Can this verification answer "out-of-network" at all? Summaries stored before
     * 2026-07-20 carry no per-network split — for those the switch is hidden and the drawer
     * shows exactly what it always did, rather than guessing.
     */
    networkSplit() {
      return hasNetworkSplit(this.summary);
    },
    /** The view actually rendered: the chosen one, or 'both' when there is no split to show. */
    shownNetwork() {
      return this.networkSplit ? resolveNetworkView(this.networkView) : 'both';
    },
    singleNetwork() {
      return this.shownNetwork !== 'both';
    },
    shownNetworkLabel() {
      return networkViewLabel(this.shownNetwork);
    },
    // The two single views lead; Both (today's drawer) sits last.
    networkOptions() {
      return ['in', 'out', 'both'].map((v) => ({ value: v, label: v === 'both' ? 'Both' : networkViewLabel(v) }));
    },
    /**
     * The office asked for a single network by default, but THIS check predates the split.
     * Say so, once, instead of silently showing both — or they will read the in-network
     * column believing it is theirs. ⚠ Re-check bills a new check; this line only informs.
     */
    preSplitNote() {
      if (this.networkSplit || resolveNetworkView(this.defaultNetworkView) === 'both') return '';
      if (!this.maximumRows.length && !this.categories.length) return '';
      return 'This check was run before benefits were split by network, so it shows in- and '
        + 'out-of-network together. Re-check the patient to see '
        + `${networkViewLabel(resolveNetworkView(this.defaultNetworkView)).toLowerCase()} benefits on their own.`;
    },
    notReportedTitle() {
      return `The payer did not report this benefit for ${this.shownNetworkLabel.toLowerCase()} care.`;
    },

    rings() {
      const src = ringSourcesForView(this.summary && this.summary.rings, this.maximumRows, this.shownNetwork);
      return RING_DEFS.map((def) => ringView(src[def.key] || null, def));
    },
    planFacts() {
      return buildPlanFacts(this.summary, this.verification);
    },
    warnings() {
      if (this.verification && Array.isArray(this.verification.Warnings)) return this.verification.Warnings;
      return (this.row && this.row.verification && this.row.verification.warnings) || [];
    },
    maximumRows() {
      const rows = (this.summary && Array.isArray(this.summary.maximums)) ? this.summary.maximums : [];
      // Normalized on READ — the `unlimited` flag on a stored row cannot be trusted. A
      // verification written before 2026-08-12 carries the raw 99999999.99 sentinel with no
      // flag at all, and /detail returns the stored Summary rather than re-presenting, so
      // without this the table prints "$99,999,999.99" in Plan and Remaining on exactly the
      // rows the rings above now render as "Unlimited".
      return rows.map(normalizeMaximumRow);
    },
    // The table as rendered: every network (today's table) or one network's figures.
    maximums() {
      return maximumsForView(this.maximumRows, this.shownNetwork);
    },
    categories() {
      return (this.summary && Array.isArray(this.summary.categories)) ? this.summary.categories : [];
    },
    categoryRows() {
      const view = this.shownNetwork;
      return this.categories.map((c) => {
        const { percent, notReported } = categoryPercentForView(c, view);
        return {
          name: c && c.name,
          percent,
          notReported,
          // Both view keeps today's label (which network's row the figure came from).
          networkText: view === 'both'
            ? (networkLabel(c && c.network) || '—')
            : (notReported ? 'Not reported' : networkViewLabel(view)),
        };
      });
    },
    waitingPeriods() {
      return (this.summary && Array.isArray(this.summary.waitingPeriods)) ? this.summary.waitingPeriods : [];
    },
    limitations() {
      return (this.summary && Array.isArray(this.summary.limitations)) ? this.summary.limitations : [];
    },

    /**
     * Plan-level notes only. `summary.planNotes` is produced by the presenter with the
     * per-code messages removed and category-label noise filtered out.
     *
     * ⚠ The fallback to `limitations` is load-bearing, not defensive padding: a Summary is
     * built and STORED at check time, so every verification recorded before this feature
     * shipped has no planNotes at all. Without the fallback those patients' drawers would
     * silently lose the section entirely until their next check.
     */
    planNotes() {
      const s = this.summary;
      if (s && Array.isArray(s.planNotes)) return s.planNotes;
      return this.limitations;
    },
    // Per-code coverage from the 271 presenter Summary — [] when the payer sent
    // no per-procedure data, which hides the whole section + its jump link.
    procedures() {
      return (this.summary && Array.isArray(this.summary.procedures)) ? this.summary.procedures : [];
    },
    /**
     * `procedures` bucketed by `category`, preserving first-appearance order of
     * both the categories and the rows within each. A null/blank category lands
     * in an "Other" bucket rather than rendering an empty header. Uses a
     * prototype-less map so a category literally named "toString"/"constructor"
     * can't collide with an inherited Object property.
     */
    /**
     * The per-CDT table, BUILT BY THE BACKEND (insurance-verification-service
     * lib/procedure-rows.js) and delivered on `detail`.procedureRows.
     *
     * ⛔ WHY THE BACKEND BUILDS IT (2026-08-24). This drawer used to map straight over
     * `summary.procedures[]` — only the codes the PAYER itemized — while the handout PDF
     * rendered the OFFICE'S configured list with a category-rate fallback. They disagreed
     * about the same verification on prod: Mint PatNum 4256 (MetLife) printed Oral Surgery as
     * D7140/D7210/D7956/D7957/D7953 on paper and showed D7140/D7240/D7880 on screen. One code
     * in common. Nilay found it by holding the printout next to the monitor.
     *
     * ⛔ Do NOT reimplement the row model here. Two implementations is what caused the bug;
     * the fix is that there is exactly one, and it lives on the server.
     */
    backendRows() {
      const d = this.activeDetail || {};
      const m = d.procedureRows;
      return (m && Array.isArray(m.groups)) ? m : null;
    },

    /**
     * `procedures` bucketed by `category`, preserving first-appearance order of both the
     * categories and the rows within each.
     *
     * Prefers the backend model. The legacy branch below runs only against a service that
     * predates procedureRows — it maps the raw payer rows into the SAME shape the template
     * expects, so the markup never has to know which branch produced it. Without that
     * normalisation an older backend would render a table of blank percentages, which is
     * worse than the bug being fixed.
     */
    procedureGroups() {
      if (this.backendRows) return this.backendRows.groups;
      const groups = [];
      const byCat = Object.create(null);
      this.procedures.forEach((p) => {
        if (!p) return;
        const raw = p.category;
        const cat = (raw === null || raw === undefined || raw === '') ? 'Other' : String(raw);
        if (!byCat[cat]) {
          byCat[cat] = { category: cat, rows: [] };
          groups.push(byCat[cat]);
        }
        byCat[cat].rows.push(this.legacyRow(p));
      });
      return groups;
    },

    // Codes the PAYER itemized that this office does not track. Collapsed by default: for
    // one prod AARP patient the payer sent 392 of them against the office's 23, and that
    // wall of rows is exactly what made this section unreadable before.
    payerExtras() {
      const m = this.backendRows;
      return (m && Array.isArray(m.payerExtras)) ? m.payerExtras : [];
    },

    payerExtraGroups() {
      const groups = [];
      const byCat = Object.create(null);
      this.payerExtras.forEach((p) => {
        if (!p) return;
        const cat = (p.category === null || p.category === undefined || p.category === '') ? 'Other' : String(p.category);
        if (!byCat[cat]) { byCat[cat] = { category: cat, rows: [] }; groups.push(byCat[cat]); }
        byCat[cat].rows.push(p);
      });
      return groups;
    },

    // Does the section have anything at all to show? Must consider BOTH sets: an office whose
    // configured codes the payer never itemized still has extras worth seeing, and vice versa.
    hasProcedureContent() {
      return this.procedureGroups.length > 0 || this.payerExtras.length > 0;
    },

    // Any row whose % came from the category rather than the code — drives the same footnote
    // the PDF prints, so the two surfaces explain the asterisk identically.
    hasEstimate() {
      return !!(this.backendRows && this.backendRows.hasEstimate);
    },

    // The hover explanation behind an estimated %. Same claim as the PDF footnote, so a
    // staff member reading the screen and a patient reading the handout are told the same thing.
    ESTIMATE_TITLE() {
      return 'Estimated from category coverage — your plan did not return a benefit specific to this code.';
    },
    // Payer-reported last-service dates from the 271 Summary (deduped +
    // most-recent-first upstream). Each row may carry `inOfficeChart` — see the template.
    summaryServiceHistory() {
      return (this.summary && Array.isArray(this.summary.serviceHistory)) ? this.summary.serviceHistory : [];
    },

    policyHolder() {
      return this.activeDetail.policyHolder || null;
    },

    /**
     * Who the payer's answer is actually ABOUT: 'self' | 'patient' | 'subscriber'.
     *
     * `summary.subjectScope` is stamped by the service at check time — but only since
     * 2026-08-11, so every row written before that has it undefined. Falling back to the
     * policy-holder identity keeps those legacy rows honest: before the dependent fix the
     * 270 always named the subscriber, so a dependent's old row IS subscriber-scoped. Without
     * this fallback the two rows on Mint prod today would silently claim to be the patient's.
     */
    subjectScope() {
      const stamped = this.summary && this.summary.subjectScope;
      if (stamped === 'self' || stamped === 'patient' || stamped === 'subscriber') return stamped;
      if (!this.policyHolder) return null;
      return this.policyHolder.isPatient ? 'self' : 'subscriber';
    },

    policyHolderName() {
      const p = this.policyHolder;
      if (!p) return '';
      return [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
    },

    /**
     * The line under "Recent services (per insurance)" naming whose record it is.
     * Silent for a self-subscriber — there is no second person to confuse them with.
     */
    historyAttribution() {
      const scope = this.subjectScope;
      const carrier = this.carrierName || 'the payer';
      if (scope === 'patient') {
        return `As reported by ${carrier} for ${this.patientName}.`;
      }
      if (scope === 'subscriber') {
        // The heading already names the holder in red, so repeating it here would be louder
        // without being clearer. This line's job is the CONSEQUENCE — that the dates below
        // belong to somebody else — plus the carrier, which the heading has no room for.
        const who = this.patientName ? `${this.patientName}'s` : "this patient's";
        return `As reported by ${carrier}. These dates are the policy holder's — not ${who} own history.`;
      }
      return '';
    },

    /**
     * Is an office-chart diff meaningful for this result at all?
     *
     * Only when the payer answered about THIS patient. On subscriber scope the rows are a
     * different human's service record, so every one of them is legitimately absent from
     * this patient's chart — Saba Khan's real Mint row flags all eight. Technically true,
     * useless, and it would drown the genuine "done elsewhere" signal precisely on
     * dependents. The attribution line says whose history it is; that is the honest answer.
     */
    showsChartDiff() {
      return this.subjectScope !== 'subscriber';
    },

    elsewhereTitle() {
      return `${this.carrierName || 'The payer'} has this service on record, but it is not in this `
        + 'office\'s completed procedures. It was likely done elsewhere and still counts toward '
        + 'the patient\'s frequency limit.';
    },

    navSections() {
      const secs = [{ id: 'iv-sec-summary', label: 'Plan summary' }];
      if (this.warnings.length) secs.push({ id: 'iv-sec-warnings', label: 'Coverage alerts' });
      if (this.maximumRows.length) secs.push({ id: 'iv-sec-maximums', label: 'Maximums & deductibles' });
      if (this.categories.length) secs.push({ id: 'iv-sec-benefits', label: 'Benefits by category' });
      if (this.hasProcedureContent) secs.push({ id: 'iv-sec-procedures', label: 'Procedure coverage' });
      if (this.waitingPeriods.length) secs.push({ id: 'iv-sec-waiting', label: 'Waiting periods' });
      // Must test planNotes, not limitations: the section renders planNotes, so keying the
      // jump link off the other array would offer a link to a section that isn't there
      // (planNotes can be empty while limitations is full — that's the normal case now).
      if (this.planNotes.length) secs.push({ id: 'iv-sec-limitations', label: 'Plan notes' });
      // 'Service history' (the OD-chart mirror) was removed 2026-08-12 along with its
      // section. It was the one unconditional entry here — a jump link that always
      // rendered — so leaving it would point at an element that no longer exists.
      if (this.summaryServiceHistory.length) secs.push({ id: 'iv-sec-summary-history', label: 'Recent services' });
      return secs;
    },

    verifyLoading() {
      return !!(this.verifyState && this.verifyState.loading);
    },
    verifyMessage() {
      return (this.verifyState && this.verifyState.message) || '';
    },
    verifyType() {
      return (this.verifyState && this.verifyState.type) || 'info';
    },
    verifyMsgIcon() {
      if (this.verifyType === 'error') return 'fa-exclamation-circle';
      if (this.verifyType === 'warning') return 'fa-exclamation-triangle';
      return 'fa-info-circle';
    },

    /**
     * Did the last check refuse for a reason no retry can fix? Drives the empty state's
     * copy and, crucially, whether it offers a "Verify now" button at all.
     *
     * Reads the RAW `reason` the page carries on verifyState (see runVerify) rather than
     * pattern-matching the rendered message — copy gets reworded, and a UI that silently
     * stops recognising a terminal refusal goes straight back to inviting a doomed click.
     */
    terminalRefusal() {
      return isTerminalVerifyReason(this.verifyState && this.verifyState.reason);
    },
  },

  watch: {
    // Never leave a confirmation armed across a close or a patient switch — a
    // stale panel would show one patient's data above another's Confirm button.
    open(val) {
      if (!val) {
        this.confirmOpen = false;
        this.pendingOrdinal = null;
        this.activeOrdinal = 1;
      }
    },
    row() {
      this.confirmOpen = false;
      this.pendingOrdinal = null;
      // Always land on the primary for a new patient. Carrying the tab over would
      // show plan 2's benefits under plan 1's patient for the frame before the
      // secondary fetch resolves — or indefinitely, if the new patient has no
      // second plan at all.
      this.activeOrdinal = 1;
    },

    /**
     * The TABLE's inline Verify button routes through THIS panel rather than
     * spending directly. That button used to call verify with no confirmation at
     * all, while the drawer's own button always confirmed — two paths to the same
     * billed call, one of them unguarded. Rather than duplicate the panel, the page
     * opens the drawer and bumps this counter.
     *
     * ⚠ A COUNTER, NOT A BOOLEAN, and set on nextTick — both deliberate:
     *   - Counter: clicking Verify on a row whose drawer is ALREADY open changes
     *     neither `open` nor `row`, so a boolean would already be `true` and no
     *     watcher would fire. Every click must re-arm.
     *   - nextTick: opening the drawer mutates `open` AND `row` in the same flush,
     *     and both of those watchers set `confirmOpen = false`. Setting it directly
     *     here would depend on watcher registration order; deferring one tick means
     *     we always land after the resets, whatever order they ran in.
     */
    confirmRequest(val) {
      if (!val) return;
      // The table's button has always meant "check this patient" — every plan —
      // so it arms an UNSCOPED check and gets the two-checks cost warning.
      this.pendingOrdinal = null;
      this.$nextTick(() => { this.confirmOpen = true; });
    },

    // Settings usually land after the drawer mounts (the page fetches them in parallel).
    // Apply the office default then — unless staff have already picked a view by hand.
    defaultNetworkView(val) {
      if (!this.networkViewPicked) this.networkView = resolveNetworkView(val);
    },

    // Reset the jump highlight + scroll to top whenever different benefit detail
    // loads into the drawer. Watches the ACTIVE plan's id, so switching tabs
    // re-homes the scroll too — the section list differs per plan (a secondary
    // often has no procedure table), and keeping the old offset would land the
    // reader in the middle of nothing.
    'activeDetail.verificationId'() {
      this.activeSection = 'iv-sec-summary';
      this.$nextTick(() => {
        if (this.$refs.scrollBody) this.$refs.scrollBody.scrollTop = 0;
      });
    },
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },

  methods: {
    ...mapActions('insuranceVerification', ['fetchPdfUrl']),

    money: formatMoney,
    procDate: formatProcDate,
    netLabel: networkLabel,
    freq: formatFrequency,
    samePct: samePercent,

    pickNetworkView(view) {
      this.networkView = resolveNetworkView(view);
      this.networkViewPicked = true;
    },

    /** A procedure row's % for the single network on screen. */
    sidePct(p) {
      return this.shownNetwork === 'in' ? p.inPercent : p.outPercent;
    },

    /**
     * Hover text for a single-network cell. A dash where the OTHER network has a figure is the
     * payer's silence, not "no coverage" — say which, so nobody quotes the missing figure as 0%.
     */
    sideTitle(p) {
      if (p.isEstimate && this.sidePct(p) !== null && this.sidePct(p) !== undefined) return this.ESTIMATE_TITLE;
      const other = this.shownNetwork === 'in' ? p.outPercent : p.inPercent;
      const mine = this.sidePct(p);
      if ((mine === null || mine === undefined) && other !== null && other !== undefined) return this.notReportedTitle;
      return null;
    },

    /** Coverage percent → "80%"; em-dash for null/blank/non-finite (0 stays "0%"). */
    /**
     * Map a RAW payer procedure (summary.procedures[]) into the same row shape the backend
     * now sends, so the template has ONE contract.
     *
     * Only reachable against a service older than 2026-08-24. It cannot reproduce the
     * category-rate fallback (that needs summary.categories and the CDT→STC map, which is
     * server-side), so those rows simply show a dash — the pre-existing behaviour, not a
     * regression. `isEstimate: false` is therefore always correct here: nothing on this path
     * was ever estimated.
     */
    legacyRow(p) {
      const r = p && typeof p === 'object' ? p : {};
      const pctOf = (side) => (side && side.coveragePercent !== null && side.coveragePercent !== undefined
        ? side.coveragePercent : null);
      return {
        code: r.code || null,
        description: r.description || null,
        category: r.category || null,
        inPercent: pctOf(r.inNetwork),
        outPercent: pctOf(r.outNetwork),
        frequency: r.frequency || null,
        remaining: r.remaining || null,
        lastServiceDate: r.lastServiceDate || null,
        notes: Array.isArray(r.notes) ? r.notes : [],
        ageLimit: r.ageLimit || null,
        isEstimate: false,
        source: 'code',
      };
    },

    /**
     * A coverage cell. Appends the estimate marker so the drawer and the handout use the
     * SAME notation — the PDF has printed “80%*” since 2026-07-20 and the screen printed
     * nothing at all for the same row, which is how the two came to disagree in the first place.
     */
    pctCell(v, isEstimate) {
      const t = this.pct(v);
      return (isEstimate && t !== '—') ? `${t}*` : t;
    },

    pct(v) {
      if (v === null || v === undefined || v === '') return '—';
      const n = Number(v);
      return Number.isFinite(n) ? `${n}%` : '—';
    },

    /** "1 of 2 left" for a procedure row, or '' when the payer said nothing. */
    left(p) {
      return formatRemaining(p && p.remaining, p && p.frequency);
    },

    /** 'red' when the benefit is used up, 'green' when uses remain. */
    leftTone(p) {
      return remainingSeverity(p && p.remaining) || 'green';
    },

    /**
     * Did this office explicitly ask about this code? Drives ONLY whether a missing frequency
     * is called out; it never changes a value we actually received.
     *
     * ⛔ Requires a non-empty explicit list. A null `procedureCodes` means the office is on
     * Teamio's default set — our choice, not their question — see the prop's comment.
     */
    wasRequested(code) {
      if (!Array.isArray(this.requestedCodes) || !this.requestedCodes.length) return false;
      if (typeof code !== 'string' || !code) return false;
      const want = code.trim().toUpperCase();
      return this.requestedCodes.some(
        (c) => typeof c === 'string' && c.trim().toUpperCase() === want
      );
    },

    /**
     * The chip line under a procedure row: the payer's own limitation notes, plus an age
     * limit when one actually constrains. Each chip carries the ORIGINAL payer string as
     * its tooltip, so a front desk can always see exactly what was sent.
     */
    noteChips(p) {
      const out = [];
      const age = formatAgeLimit(p && p.ageLimit);
      if (age) out.push({ label: age, tone: 'neutral', title: age });
      const notes = p && Array.isArray(p.notes) ? p.notes : [];
      notes.forEach((n) => {
        const label = noteChipLabel(n);
        if (!label) return;
        out.push({
          label,
          tone: noteChipTone(n),
          title: (n && typeof n.raw === 'string' && n.raw) || label,
        });
      });
      return out;
    },

    /** OD 'YYYYMMDD' → 'MM/DD/YYYY'. Anything else passes through unchanged. */
    odDob(value) {
      if (!value) return '';
      const s = String(value);
      const m = s.match(/^(\d{4})(\d{2})(\d{2})$/);
      return m ? `${m[2]}/${m[3]}/${m[1]}` : s;
    },

    onKeydown(e) {
      // Escape backs out of the confirmation first, so a mis-aimed Escape can't
      // close the whole drawer when the user only meant to cancel the check.
      if (e.key !== 'Escape' || !this.open) return;
      if (this.confirmOpen) {
        this.confirmOpen = false;
        return;
      }
      this.$emit('close');
    },

    // ── Verify confirmation ────────────────────────────────────────────────
    /**
     * Step 1 of Verify Now: show what will be sent instead of spending.
     *
     * We deliberately do NOT hard-disable the confirm button for a row with a
     * `dataIssue`. The BACKEND is the gate — STEP 1.5 of runEligibilityCheck
     * refuses the check before any Stedi call, so clicking through costs nothing
     * and returns a clear `blocked_bad_data` message. Blocking it here instead
     * would make the frontend an authority it isn't: this row is a snapshot that
     * can be minutes old, so a user who just fixed the record in Open Dental
     * would be stranded behind a page refresh they have no reason to know about.
     * We warn loudly and demote the button; we don't lock the door.
     */
    requestVerify(ordinal) {
      if (this.verifyLoading) return;
      // A DOM click handler would pass the MouseEvent as the first arg, which would
      // then be forwarded to the backend as an `ordinal` and rejected. Only accept a
      // real plan slot; anything else means "all plans".
      this.pendingOrdinal = (ordinal === 1 || ordinal === 2) ? ordinal : null;
      this.confirmOpen = true;
    },
    confirmVerify() {
      if (this.verifyLoading) return;
      this.confirmOpen = false;
      this.$emit('verify', { ordinal: this.pendingOrdinal });
    },
    cancelVerify() {
      // Nothing was sent and nothing was billed.
      this.confirmOpen = false;
      this.pendingOrdinal = null;
    },

    /**
     * Switch which plan's benefits the body shows, fetching the secondary's detail
     * the first time it is asked for. The page owns fetchDetail (it owns siteID), so
     * this asks rather than fetches.
     *
     * Cancels an armed confirmation: the panel shows one plan's facts above a
     * Confirm button, and changing plans underneath it would put the wrong carrier
     * and member ID over a button that spends money.
     */
    selectPlan(ordinal) {
      const next = ordinal === 2 ? 2 : 1;
      if (next === this.activeOrdinal) return;
      this.confirmOpen = false;
      this.pendingOrdinal = null;
      this.activeOrdinal = next;
      if (next === 2) this.$emit('load-plan', 2);
    },

    scrollTo(id) {
      this.activeSection = id;
      const scope = this.$refs.scrollBody;
      const el = scope && scope.querySelector(`#${id}`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    warnColor(w) {
      return chipColors(w && w.severity).color;
    },
    warnStyle(w) {
      const c = chipColors(w && w.severity);
      return { background: c.bg, border: `1px solid ${c.border}` };
    },
    warnIcon(w) {
      return chipForCode(w && w.code).icon;
    },
    remainingColor(m) {
      if (!m.hasRemaining) return '#717977';
      const rem = Number(m.remainingAmount);
      if (Number.isFinite(rem) && rem <= 0) return '#e5484d';
      return '#0f7259';
    },

    /**
     * Fetch a presigned URL for the benefits handout and open it. Both buttons
     * share one endpoint; the presign is inline-disposition, so 'download' clicks
     * an anchor (download hint) and 'print' opens a new tab the user prints from.
     * A 409 (non-verified — shouldn't happen given verified-only visibility) is a
     * quiet info toast; anything else is a friendly error.
     */
    async openPdf(action) {
      if (this.pdfBusy) return;
      const verificationID = this.verificationID;
      if (!this.siteId || !verificationID) {
        if (this.$toast) this.$toast.error('No verification to open yet.');
        return;
      }
      this.pdfBusy = action;
      try {
        const data = await this.fetchPdfUrl({ siteID: this.siteId, verificationID });
        const url = data && data.url;
        if (!url) throw new Error('No PDF URL returned');
        if (action === 'download') {
          const a = document.createElement('a');
          a.href = url;
          a.target = '_blank';
          a.rel = 'noopener';
          a.download = '';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          window.open(url, '_blank', 'noopener');
        }
      } catch (err) {
        const status = err && err.response && err.response.status;
        if (status === 409) {
          if (this.$toast) this.$toast.info('No benefits handout is available for this verification yet.');
        } else if (status === 403) {
          if (this.$toast) this.$toast.warning("Insurance Verification isn't enabled for this practice.");
        } else if (this.$toast) {
          this.$toast.error('Couldn’t open the PDF. Please try again in a moment.');
        }
      } finally {
        this.pdfBusy = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes ivScrim {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes ivSlide {
  from { transform: translateX(24px); opacity: 0.4; }
  to { transform: translateX(0); opacity: 1; }
}

.iv-drawer-scrim {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(12, 15, 14, 0.42);
  animation: ivScrim 0.2s ease;
}

.iv-drawer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  /* Was min(1080px, 95vw) — roughly half the screen on a large monitor, which could not
     hold the per-code limitation chips. Nilay asked for >= 80%; the cap keeps 42 rows of
     small text from stretching unreadably wide on an ultra-wide display. */
  width: min(1800px, 88vw);
  background: #ffffff;
  box-shadow: -12px 0 40px rgba(12, 15, 14, 0.18);
  display: flex;
  flex-direction: column;
  animation: ivSlide 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  color: #0c0f0e;
}

// ── Header ──────────────────────────────────────────────
.iv-drawer-head {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 26px;
  border-bottom: 1px solid #dfe4e3;
  flex: none;
}

.iv-drawer-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #cdede2;
  color: #0b5c49;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 15px;
  flex: none;
}

.iv-drawer-headmeta {
  flex: 1;
  min-width: 0;
}

.iv-drawer-titlerow {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.iv-drawer-name {
  margin: 0;
  font-weight: 800;
  font-size: 23px;
  letter-spacing: -0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.iv-drawer-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  flex: none;
}

.iv-drawer-checknote {
  margin-top: 3px;
  color: #717977;
  font-size: 12px;
  line-height: 1.35;
}

.iv-drawer-sub {
  font-size: 13px;
  color: #717977;
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.iv-drawer-actions {
  display: flex;
  align-items: center;
  gap: 9px;
  flex: none;
}

.iv-drawer-close {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  border: none;
  background: transparent;
  color: #717977;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { background: #eceff0; color: #0c0f0e; }
}

// ── Buttons ─────────────────────────────────────────────
.iv-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s ease;
  white-space: nowrap;

  &:disabled { opacity: 0.55; cursor: not-allowed; }
}

.iv-btn--secondary {
  border: 1px solid #c8cfcd;
  background: #ffffff;
  color: #0b5c49;

  &:not(:disabled):hover { border-color: #0b5c49; background: #e9f6f1; }
}

.iv-btn--primary {
  border: 1px solid #0f7259;
  background: #0f7259;
  color: #ffffff;

  &:not(:disabled):hover { background: #0b5c49; border-color: #0b5c49; }
}

.iv-btn--ghost {
  border: 1px solid transparent;
  background: transparent;
  color: #717977;
  text-decoration: underline;

  &:not(:disabled):hover { color: #262b2a; background: #eceff0; }
}

// ── Verify confirmation panel ───────────────────────────
.iv-confirm {
  flex: none;
  padding: 16px 26px 18px;
  border-bottom: 1px solid #dfe4e3;
  background: #fafbfb;

  &.is-blocked {
    background: #fef6e7;
    border-bottom-color: #fde9c8;
  }
}

.iv-confirm-head {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 4px;

  .fa { color: #0f7259; }

  .is-blocked & .fa { color: #b45309; }
}

.iv-confirm-title {
  font-weight: 700;
  font-size: 15px;
  color: #262b2a;

  .is-blocked & { color: #8a5a12; }
}

.iv-confirm-block {
  margin: 10px 0 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #fde9c8;
}

.iv-confirm-block-detail {
  font-weight: 700;
  font-size: 14px;
  color: #8a5a12;
}

.iv-confirm-block-fix {
  font-size: 13px;
  color: #545b59;
  margin-top: 4px;
  line-height: 1.45;
}

// The "you already checked this" note. Informational blue, deliberately NOT the
// amber of .iv-confirm-block — a redundant re-check is a spending question, not a
// data problem the office has to go fix in Open Dental. Reusing amber here would
// blur the one signal that means "stop, this record is wrong".
.iv-confirm-redundant {
  margin: 10px 0 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #cfe2f3;
  font-size: 13px;
  color: #2c5777;
  line-height: 1.45;
  max-width: 640px;

  strong { color: #1b3f59; }
}

.iv-confirm-sub {
  font-size: 13px;
  color: #717977;
  margin-bottom: 10px;
  line-height: 1.45;
  max-width: 640px;
}

.iv-confirm-facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1px;
  background: #dfe4e3;
  border: 1px solid #dfe4e3;
  border-radius: 10px;
  overflow: hidden;
}

.iv-confirm-fact {
  background: #ffffff;
  padding: 9px 13px;
  min-width: 0;
}

.iv-confirm-fact-k {
  font-size: 11px;
  color: #9ca4a2;
  margin-bottom: 3px;
}

.iv-confirm-fact-v {
  font-size: 13.5px;
  font-weight: 700;
  word-break: break-word;

  &.is-flagged { color: #b45309; }
}

.iv-confirm-note {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-top: 9px;
  font-size: 12.5px;
  color: #9ca4a2;
  line-height: 1.4;

  .fa { margin-top: 2px; flex: none; }
}

// The cost warning is the one note that must not read as grey boilerplate — it is
// telling the office this click costs twice as much as it looks like.
.iv-confirm-note--cost {
  color: #8a6100;
  background: #fff8e6;
  border: 1px solid #f2e0b0;
  border-radius: 6px;
  padding: 7px 9px;
}

.iv-confirm-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  margin-top: 14px;
}

// ── Verify inline message ───────────────────────────────
.iv-drawer-verifymsg {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 26px;
  font-size: 13.5px;
  flex: none;

  &.is-info { background: #eff5ff; color: #1e40af; }
  &.is-warning { background: #fef6e7; color: #b45309; }
  &.is-error { background: #fdeeee; color: #a5292e; }
}

// ── Plan tabs (primary / secondary) ─────────────────────
// Only rendered for a dual-plan patient, so a single-plan drawer is unchanged.
.iv-plan-tabs {
  flex: none;
  display: flex;
  align-items: stretch;
  gap: 4px;
  padding: 0 26px;
  border-bottom: 1px solid #dfe4e3;
  background: #fafbfb;
}

.iv-plan-tab {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 9px 14px 8px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #717977;
  font-size: 13.5px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;

  &:hover { color: #262b2a; }

  &.is-active {
    color: #1c7f74;
    border-bottom-color: #1c7f74;
  }
}

// The carrier name under each tab label — this is what actually tells the front
// desk which plan they're looking at, so it stays visible on the inactive tab too.
.iv-plan-tab-sub {
  font-size: 11.5px;
  font-weight: 500;
  color: #8a918f;
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.iv-plan-tab-action {
  margin-left: auto;
  align-self: center;
  border: 1px solid #dfe4e3;
  border-radius: 6px;
  background: #fff;
  color: #717977;
  font-size: 12.5px;
  padding: 5px 10px;
  cursor: pointer;

  &:not(:disabled):hover { color: #262b2a; border-color: #c9d1cf; }
  &:disabled { opacity: 0.55; cursor: default; }
}

// ── Body ────────────────────────────────────────────────
.iv-drawer-body {
  flex: 1;
  display: flex;
  min-height: 0;
}

.iv-drawer-loaded {
  flex: 1;
  display: flex;
  min-height: 0;
}

.iv-drawer-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 24px;
  color: #717977;
  font-size: 14px;
  text-align: center;
}

.iv-drawer-state--error { color: #a5292e; }

.iv-drawer-state-icon {
  font-size: 34px;
  color: #c8cfcd;
}

.iv-drawer-state-title {
  font-weight: 700;
  font-size: 17px;
  color: #262b2a;
}

.iv-drawer-state-sub {
  font-size: 13.5px;
  color: #717977;
  max-width: 380px;
}

// ── Jump-to nav ─────────────────────────────────────────
.iv-jump-nav {
  width: 216px;
  flex: none;
  border-right: 1px solid #dfe4e3;
  padding: 18px 12px;
  overflow: auto;
  background: #fafbfb;
}

.iv-jump-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9ca4a2;
  padding: 0 10px 10px;
}

.iv-jump-link {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 9px 10px;
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 600;
  color: #545b59;
  cursor: pointer;

  &:hover { background: #eceff0; }
  &.is-active { background: #e9f6f1; color: #0b5c49; }
}

// ── Content scroll ──────────────────────────────────────
.iv-drawer-body-scroll {
  flex: 1;
  overflow: auto;
  padding: 26px 30px 60px;
}

.iv-sec {
  margin-bottom: 26px;
  scroll-margin-top: 20px;
}

.iv-sec-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #0f7259;
  margin-bottom: 14px;
}

.iv-rings-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
  margin-bottom: 22px;
}

.iv-facts-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: #dfe4e3;
  border: 1px solid #dfe4e3;
  border-radius: 12px;
  overflow: hidden;
}

.iv-fact {
  background: #ffffff;
  padding: 14px 16px;
  min-width: 0;
}

.iv-fact-k {
  font-size: 11.5px;
  color: #9ca4a2;
  margin-bottom: 5px;
}

.iv-fact-v {
  font-size: 14px;
  font-weight: 700;
  word-break: break-word;
}

// ── Warnings ────────────────────────────────────────────
.iv-warn-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.iv-warn-row {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;

  .fa { margin-top: 1px; flex: none; }
}

.iv-warn-title {
  font-weight: 700;
  font-size: 14px;
  color: #262b2a;
}

.iv-warn-detail {
  font-size: 13px;
  color: #545b59;
  margin-top: 2px;
  line-height: 1.45;
}

// ── Tables (maximums / categories / history) ────────────
// .iv-htable* is gone with the OD-chart "Service history" section (2026-08-12).
// .iv-hcode stays — the procedure-coverage table still uses it.
.iv-mtable,
.iv-ctable,
.iv-ptable,
.iv-shtable {
  border: 1px solid #dfe4e3;
  border-radius: 12px;
  overflow: hidden;
}

.iv-mtable-head,
.iv-mtable-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 12px 16px;
  align-items: center;
}

.iv-ctable-head,
.iv-ctable-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1.2fr;
  gap: 12px;
  padding: 11px 16px;
  align-items: center;
}

.iv-ptable-head,
.iv-ptable-row {
  display: grid;
  /* The extra drawer width goes to the procedure name and the allowance cell (which now
     carries the remaining pill), NOT to new columns — six fact columns is the limit for
     scanning a value straight down, and the limitations live on the chip line below. */
  grid-template-columns: 74px minmax(200px, 1.4fr) 52px 52px minmax(170px, 0.7fr) 96px;
  gap: 10px;
  padding: 11px 16px;
  align-items: center;
}

/* Row wrapper: the facts line plus its optional chip line, so a code and its notes read as
   ONE row. No border here — .iv-ptable-row further down already carries the row separator,
   and adding one would double the rule between every pair of codes. */

/* "1 of 2 left" — green while uses remain, RED at zero. Zero is never hidden: it is the
   fact that stops a practice booking an appointment the payer will deny. */
// Deliberately quiet: an absent frequency is information, not an alarm. Amber here would
// read as "something is wrong with this plan" when the plan is fine and the payer simply
// did not say.
.iv-freq-unreported {
  color: #9ca4a2;
  font-style: italic;
  cursor: help;
}

.iv-left-pill {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.iv-left-pill.is-green {
  background: #e7f5f0;
  color: #0f7259;
}
.iv-left-pill.is-red {
  background: #fdecec;
  color: #b42318;
}

/* The per-code limitation chips. Indented to sit under the procedure name, and absent
   entirely (no height) when a code has no notes. */
.iv-pnotes {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 16px 10px 100px;
}

.iv-nchip {
  display: inline-block;
  max-width: 100%;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  line-height: 1.5;
  border: 1px solid transparent;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.iv-nchip.is-neutral {
  background: #f4f6f5;
  color: #5b6663;
  border-color: #e3e8e6;
}
.iv-nchip.is-good {
  background: #f0f9f5;
  color: #0f7259;
  border-color: #cfe9df;
}
.iv-nchip.is-warn {
  background: #fdf6e9;
  color: #92600a;
  border-color: #f3e2bf;
}

.iv-shtable-head,
.iv-shtable-row {
  display: grid;
  grid-template-columns: 1fr 130px;
  gap: 12px;
  padding: 11px 16px;
  align-items: center;
}

.iv-mtable-head,
.iv-ctable-head,
.iv-ptable-head,
.iv-shtable-head {
  background: #fafbfb;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #717977;
}

.iv-mtable-row,
.iv-ctable-row,
.iv-ptable-row,
.iv-shtable-row {
  border-top: 1px solid #dfe4e3;
  font-size: 13.5px;
}

// The policy holder's name inside a section title. Natural case against the all-caps
// heading, which makes it read as a NAME and stand out more than shouting it would.
.iv-holder {
  text-transform: none;
  letter-spacing: 0.01em;
  font-size: 14px;
}

// RED only when the holder is NOT this patient — the case a reader must not miss. Colouring
// a self-subscriber's own name would make red mean "a name follows" instead of "careful,
// different person", and the warning would stop working within a day of use.
.iv-holder--other {
  color: #c0392b;
}

.iv-title-sep {
  color: #9aa5a2;
  font-weight: 400;
  margin: 0 2px;
}

// Attribution line under a section title — whose record the rows below describe.
.iv-sec-sub {
  margin: -4px 0 10px;
  font-size: 12.5px;
  color: #717977;
}

// The degrade case: these benefits belong to the POLICY HOLDER, not the patient whose name
// is at the top of the drawer. Amber because it changes how the numbers should be read —
// deliberately louder than the neutral grey of the normal attribution.
.iv-sec-sub--warn {
  color: #92600a;
  background: #fdf6e7;
  border: 1px solid #f3e2bf;
  border-radius: 8px;
  padding: 7px 10px;
  margin-top: 0;
}

// "not in this office's chart" — the payer paid for something we have no record of doing.
// Informational, not an error: it prompts a look, it does not report a fault.
.iv-elsewhere {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 7px;
  border-radius: 999px;
  background: #eef3fb;
  border: 1px solid #d3e0f2;
  color: #3d5a80;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  vertical-align: middle;
  cursor: help;
}

// Category subheader inside the procedure-coverage table (a full-width block,
// so it spans all grid columns of the rows it groups).
.iv-ptable-group {
  padding: 8px 16px;
  background: #f4f7f6;
  border-top: 1px solid #dfe4e3;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #545b59;
}

// The allowance cell now holds the frequency text AND the remaining pill side by side.
.iv-pfreq {
  color: #545b59;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.iv-mlabel,
.iv-cname { font-weight: 600; }
.iv-mremaining { font-weight: 700; }
// "No maximum" sits in the money column but is words, not a figure — deliberately NOT
// .iv-mono, so it never lines up as though it were a dollar amount.
.iv-nomax { color: #545b59; font-style: italic; }
.iv-ccov { font-weight: 700; }
/* An ESTIMATE: this % came from the plan's category rate, not a benefit the payer returned
   for this specific code. Matches the handout PDF's treatment (oblique + amber + a trailing
   ‘*’ + the same footnote) on purpose — staff read the screen while the patient reads the
   paper, and the two must not describe the same number differently. */
.iv-ccov.is-estimate {
  font-style: italic;
  font-weight: 600;
  color: #b45309;
  cursor: help;
}

.iv-pfootnote {
  margin: 8px 2px 0;
  font-size: 11px;
  color: #717977;
}

.iv-pextras { margin-top: 14px; }
.iv-pextras-toggle {
  background: none;
  border: 0;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  color: #0f7259;
  cursor: pointer;

  i { margin-right: 6px; }
  &:hover { text-decoration: underline; }
}
.iv-pextras-hint {
  margin: 2px 0 8px;
  font-size: 11px;
  color: #9ca4a2;
}
.iv-cnet { color: #717977; font-size: 13px; }
.iv-hcode { font-weight: 600; color: #0b5c49; }

.iv-right { text-align: right; }
.iv-mono { font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace; }
.iv-muted { color: #9ca4a2; }

// ── Network view switch (2026-09-29) ────────────────────
// Sticky at the top of the scroll body. The negative margins cancel the body's own padding
// (26px 30px) so the bar spans edge to edge and rows never show through beside it.
// ⛔ top is -26px, NOT 0: a sticky box sticks inside the scroll container's PADDING, so `top: 0`
// parked the bar 26px down and scrolled rows showed through the strip above it (seen on dev,
// 2026-09-29). -26px = the body's padding-top, which puts the bar flush with the edge.
.iv-netbar {
  position: sticky;
  top: -26px;
  z-index: 3;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
  margin: -26px -30px 22px;
  padding: 12px 30px;
  background: #ffffff;
  border-bottom: 1px solid #dfe4e3;
}

.iv-netbar-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #545b59;
}

.iv-seg {
  display: inline-flex;
  padding: 3px;
  border-radius: 10px;
  background: #eceff0;
}

.iv-seg-btn {
  border: none;
  background: transparent;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 600;
  color: #545b59;
  cursor: pointer;

  &:hover { color: #262b2a; }
  &.is-active {
    background: #ffffff;
    color: #0b5c49;
    box-shadow: 0 1px 3px rgba(12, 15, 14, 0.14);
  }
}

.iv-netbar-note {
  font-size: 12.5px;
  color: #9ca4a2;
}

.iv-netbar-presplit {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: 20px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f4f6f6;
  font-size: 13px;
  color: #545b59;
  line-height: 1.45;

  .fa { margin-top: 2px; color: #717977; }
}

// Room for the sticky bar when a Jump-to link scrolls a section to the top.
.iv-drawer-body-scroll.has-netbar .iv-sec { scroll-margin-top: 70px; }

// Which network a section's figures belong to — on every section the view changes.
.iv-net-pill {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 9px;
  border-radius: 999px;
  background: #e9f6f1;
  color: #0b5c49;
  font-size: 11px;
  letter-spacing: 0.04em;
  vertical-align: 1px;
}

.iv-netlegend {
  margin: -6px 2px 10px;
  font-size: 12.5px;
  color: #717977;
}

.iv-netlegend-diff,
.iv-ptable-row.is-netdiff {
  background: #eef4fc;
  box-shadow: inset 3px 0 0 #3b82f6;
}

.iv-netlegend-diff { padding: 1px 6px 1px 8px; border-radius: 4px; }

// Both view: one figure spanning the In % and Out % columns when they agree.
.iv-pct-merged {
  grid-column: span 2;
  text-align: center;
}

// Single-network view: one % column instead of two.
.iv-ptable.is-single .iv-ptable-head,
.iv-ptable.is-single .iv-ptable-row {
  grid-template-columns: 74px minmax(200px, 1.4fr) 64px minmax(170px, 0.7fr) 96px;
}

// ── Waiting periods ─────────────────────────────────────
.iv-waiting-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.iv-waiting-row {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 13px 15px;
  border: 1px solid #dfe4e3;
  border-radius: 11px;

  .fa { flex: none; }
}

.iv-waiting-name {
  flex: 1;
  font-weight: 600;
  font-size: 13.5px;
}

.iv-waiting-text {
  font-weight: 700;
  font-size: 13.5px;
}

// ── Limitations ─────────────────────────────────────────
.iv-limit-list {
  margin: 0;
  padding: 16px 18px 16px 34px;
  border: 1px solid #dfe4e3;
  border-radius: 12px;
  background: #ffffff;

  li {
    font-size: 13.5px;
    color: #545b59;
    line-height: 1.5;
    margin-bottom: 6px;

    &:last-child { margin-bottom: 0; }
  }
}

.iv-empty-inline {
  padding: 18px 16px;
  border: 1px dashed #dfe4e3;
  border-radius: 12px;
  color: #9ca4a2;
  font-size: 13.5px;
  text-align: center;
}
</style>
